import api from '../../../app/api';
import { PaginatedList } from '../../../shared/models/paginated-list';
import { TraceLogs } from '../../../shared/models/trace-logs';
import { transformTraceLogsResponse } from '../../../shared/utils/transform-trace-logs-response';
import { User } from '../models/user';
import { UserPasswordModel } from '../models/user-password-model';
import { UserSetting } from '../models/user-setting';
import { UserTotp } from '../models/user-totp';
import { UserTotpCode } from '../models/user-totp-code';

export const userApi = api
  .enhanceEndpoints({
    addTagTypes: ['User', 'UserSettings', 'TraceLogs'],
  })
  .injectEndpoints({
    endpoints: builder => ({
      getUser: builder.query<User, void>({
        query: () => ({
          url: '/portal-api/v1/user',
        }),
        providesTags: ['User'],
      }),
      getUserIframeUrl: builder.query<string, void>({
        query: () => ({
          url: '/portal-api/v1/user/iframe-url',
        }),
        providesTags: ['User'],
        transformResponse: (response: { url: string }) => response.url,
      }),
      purgeUserTasks: builder.mutation<void, void>({
        query: body => ({
          url: '/portal-api/v1/user/purge-tasks',
          method: 'POST',
          body,
        }),
      }),
      getUserTraceLogs: builder.query<PaginatedList<TraceLogs>, { page: number }>({
        query: params => ({
          url: '/portal-api/v1/user/trace-logs',
          params,
        }),
        providesTags: ['TraceLogs'],
        transformResponse: transformTraceLogsResponse,
      }),
      editUser: builder.mutation<User, Pick<User, 'name' | 'id'>>({
        query: body => ({
          url: '/portal-api/v1/user',
          method: 'PUT',
          body,
        }),
        invalidatesTags: ['User'],
      }),
      editUserEmail: builder.mutation<User, Pick<User, 'email'>>({
        query: body => ({
          url: '/portal-api/v1/user/email',
          method: 'PUT',
          body,
        }),
        invalidatesTags: ['User'],
      }),
      editUserPassword: builder.mutation<User, UserPasswordModel>({
        query: userPassword => ({
          url: '/portal-api/v1/user/password',
          method: 'PUT',
          body: userPassword,
        }),
        invalidatesTags: ['User'],
      }),
      getUserTotp: builder.query<UserTotp, void>({
        query: () => ({
          url: '/portal-api/v1/user/totp',
        }),
      }),
      enableUserTotp: builder.mutation<void, UserTotpCode>({
        query: editTotp => ({
          url: '/portal-api/v1/user/totp',
          method: 'PUT',
          body: editTotp,
        }),
        invalidatesTags: ['User'],
      }),
      disableUserTotp: builder.mutation<void, void>({
        query: () => ({
          url: '/portal-api/v1/user/reset-2fa',
          method: 'POST',
        }),
        invalidatesTags: ['User'],
      }),
      saveUserSetting: builder.mutation<void, UserSetting>({
        query: settings => ({
          url: '/portal-api/v1/user/settings',
          method: 'PUT',
          body: settings,
        }),
        invalidatesTags: ['UserSettings'],
      }),
      getUserSettings: builder.query<Record<string, string>, void>({
        query: () => ({
          url: '/portal-api/v1/user/settings',
        }),
        providesTags: ['UserSettings'],
      }),
      createNotification: builder.mutation<void, { token: string }>({
        query: notificationToken => ({
          url: '/portal-api/v1/user/notification-token',
          method: 'POST',
          body: notificationToken,
        }),
      }),
      deleteNotification: builder.mutation<void, string>({
        query: notificationToken => ({
          url: `/portal-api/v1/user/notification-token/${notificationToken}`,
          method: 'DELETE',
        }),
      }),
    }),
    overrideExisting: false,
  });

export const {
  useGetUserQuery,
  useGetUserTraceLogsQuery,
  useLazyGetUserIframeUrlQuery,
  usePurgeUserTasksMutation,
  useEditUserMutation,
  useEditUserPasswordMutation,
  useGetUserTotpQuery,
  useEnableUserTotpMutation,
  useDisableUserTotpMutation,
  useEditUserEmailMutation,
  useSaveUserSettingMutation,
  useGetUserSettingsQuery,
  useCreateNotificationMutation,
  useDeleteNotificationMutation,
} = userApi;
